import {ChangeDetectorRef, Component, OnInit} from '@angular/core';
import {Store} from "@ngrx/store";
import * as fromRoot from "../../../../state/app.state";
import {
  ClearState,
  FetchContractDetailsRequest,
  UpdateContractDetailsRequest,
} from '../core/contracts.actions';
import { ActivatedRoute, Router } from '@angular/router';
import { filter, skip, Subject, takeUntil } from 'rxjs';
import { contractDetailsInfo } from '../core/contracts.selectors';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { CommonService } from '../../../shared/services/common.service';
import { ContractsModel } from '../../../../models/contracts.model';
import {
  profile,
  selectedUserProfile,
  weekInfoForContract
} from "../../../shared/core/shared.selectors";
import {ClearWeekInfoForContract} from "../../../shared/core/shared.actions";
import {Location} from "@angular/common";
import {FetchClientsListRequest} from "../../clients-list/core/clients-list.actions";

@Component({
  selector: 'app-contract-screen',
  templateUrl: './contract-screen.component.html',
  styleUrls: ['./contract-screen.component.css'],
})
export class ContractScreenComponent implements OnInit {
  weekNumber: number | null;
  participantName: string;
  sessionNumber: number;
  errorMessages: any = {};
  errorMessageMap = {
    rlGoal: {},
    paGoalDay: {},
    paGoalPoints: {},
    stGoalHr: {},
    stGoalMin: {},
    strategy1: {},
    strategy2: {},
    strategy3: {},
    participantReward: {},
    adultReward: {},
    adultName: {},
    support1: {},
    support2: {},
    support3: {},
    support4: {},
    support5: {},
    support6: {},
    support7: {},
    support8: {},
    support9: {},
    support10: {},
    support11: {},
    support12: {},
    support13: {},
    support14: {},
    supportOther: {},
    supportOtherText: {},
    childSignatureText: {
      required: 'This field is required',
    },
    adultSignatureText: {
      required: 'This field is required',
    },
    adultSignature: {},
    childSignature: {},
  };
  form = new FormGroup<any>({
    rlGoal: new FormControl({ value: null, disabled: false }),
    paGoalDay: new FormControl({ value: null, disabled: false }),
    paGoalPoints: new FormControl({ value: null, disabled: false }),
    stGoalHr: new FormControl({ value: null, disabled: false }),
    stGoalMin: new FormControl({ value: null, disabled: false }, [
      Validators.min(0),
      Validators.max(59),
    ]),
    strategy1: new FormControl({ value: '', disabled: false }),
    strategy2: new FormControl({ value: '', disabled: false }),
    strategy3: new FormControl({ value: '', disabled: false }),
    participantReward: new FormControl({ value: '', disabled: false }),
    adultReward: new FormControl({ value: '', disabled: false }),
    adultName: new FormControl({ value: '', disabled: false }),
    supportOther: new FormControl({ value: false, disabled: false }),
    supportOtherText: new FormControl({ value: '', disabled: true }),
    childSignature: new FormControl({ value: false, disabled: false }),
    childSignatureText: new FormControl({ value: '', disabled: true }),
    adultSignature: new FormControl({ value: false, disabled: false }),
    adultSignatureText: new FormControl({ value: '', disabled: true }),
  });
  contractDetails: ContractsModel;
  isContractSaved: boolean;
  isContractDirty: boolean = false;
  contractDetailsLoading: boolean;
  role: number;
  selectedUserRole: number;
  selectedUserGroupId: number;
  groupId: number;
  userId: number;
  selectedUserId: number;
  stGoal: number;
  weekEndDate: string;
  weekStartDate: string;
  previousUrl: string;
  larAdult: any;
  private readonly onDestroy: Subject<any> = new Subject<any>();

  constructor(
    private commonService: CommonService,
    private store: Store<fromRoot.State>,
    private route: ActivatedRoute,
    private location: Location,
    private router: Router,
    private cdRef: ChangeDetectorRef,
  ) {
    this.store.dispatch(ClearState());

    this.store
      .select(weekInfoForContract)
      .pipe(
        takeUntil(this.onDestroy),
        filter((data) => !!data),
      )
      .subscribe((data) => {
        if (data) {
          this.weekNumber = data?.weekNumber;
          this.weekEndDate = data?.weekEndDate;
          this.weekStartDate = data?.weekStartDate;
          this.sessionNumber = data?.sessionNumber;
          this.previousUrl = data?.previousUrl;

          this.router.navigate([], {
            queryParams: {
              weekNumber: this.weekNumber,
            },
          });
        }
      });

    this.store
      .select(profile)
      .pipe(takeUntil(this.onDestroy))
      .subscribe((data) => {
        this.role = data?.role;
        this.groupId = data?.groupId;
        this.userId = data?.id;

        if (data && data.adults) {
          this.larAdult = data.adults.find((adult) => adult.isLar === 1);
          this.form.patchValue({
            adultName: `${this.larAdult.firstName} ${this.larAdult.lastName}`,
          });
        }

        this.fetchContractDetails();
      });

    this.store
      .select(selectedUserProfile)
      .pipe(takeUntil(this.onDestroy))
      .subscribe((data) => {
        this.selectedUserRole = data?.profile?.role;
        this.selectedUserGroupId = data?.profile?.groupId;
        this.selectedUserId = data?.profile?.id;

        if (data.profile && data.profile.adults) {
          this.larAdult = data.profile.adults.find(
            (adult) => adult.isLar === 1,
          );

          if (this.larAdult) {
            this.form.patchValue({
              adultName: `${this.larAdult.firstName} ${this.larAdult.lastName}`,
            });
          }
        }

        this.fetchContractDetails();
      });
  }

  fetchContractDetails() {
    if (this.route.snapshot.params['memberId'] && this.selectedUserGroupId) {
      this.store.dispatch(
        FetchContractDetailsRequest({
          payload: {
            weekNumber: this.weekNumber,
            groupId: this.selectedUserGroupId,
            forUserId: this.selectedUserId,
          },
        }),
      );
    } else if (this.groupId) {
      this.store.dispatch(
        FetchContractDetailsRequest({
          payload: {
            weekNumber: this.weekNumber,
            groupId: this.groupId,
          },
        }),
      );
    }
  }

  ngOnInit() {
    this.commonService.onNavigationEnd
      .pipe(takeUntil(this.onDestroy))
      .subscribe((d) => {
        const urlParams = {
          params: this.route.snapshot.params,
          queryParams: this.route.snapshot.queryParams,
        };

        const prevUrlParams = this.commonService.urlParams.getValue();

        if (
          d.isRouteChanged ||
          JSON.stringify(prevUrlParams) !== JSON.stringify(urlParams)
        ) {
          this.commonService.urlParams.next(urlParams);
        }
      });

    if (this.sessionNumber >= 4) {
      this.form.addControl(
        'support1',
        new FormControl({ value: true, disabled: false }),
      );
      this.form.addControl(
        'support2',
        new FormControl({ value: false, disabled: false }),
      );
      this.form.addControl(
        'support3',
        new FormControl({ value: false, disabled: false }),
      );
      this.form.addControl(
        'support4',
        new FormControl({ value: false, disabled: false }),
      );
      this.form.addControl(
        'support5',
        new FormControl({ value: false, disabled: false }),
      );
      this.form.addControl(
        'support6',
        new FormControl({ value: false, disabled: false }),
      );
      this.form.addControl(
        'support7',
        new FormControl({ value: false, disabled: false }),
      );
    }

    if (this.sessionNumber >= 9) {
      this.form.addControl(
        'support8',
        new FormControl({ value: false, disabled: false }),
      );
      this.form.addControl(
        'support9',
        new FormControl({ value: false, disabled: false }),
      );
      this.form.addControl(
        'support10',
        new FormControl({ value: false, disabled: false }),
      );
    }

    if (this.sessionNumber >= 17) {
      this.form.addControl(
        'support11',
        new FormControl({ value: false, disabled: false }),
      );
      this.form.addControl(
        'support12',
        new FormControl({ value: false, disabled: false }),
      );
      this.form.addControl(
        'support13',
        new FormControl({ value: false, disabled: false }),
      );
      this.form.addControl(
        'support14',
        new FormControl({ value: false, disabled: false }),
      );
    }

    this.store
      .select(contractDetailsInfo)
      .pipe(takeUntil(this.onDestroy))
      .subscribe((data) => {
        this.contractDetails = data?.contractDetails;
        this.participantName = data?.contractDetails?.participantName;
        this.contractDetailsLoading = data?.loading;
        this.form.patchValue(
          {
            rlGoal: data?.contractDetails?.rlGoal,
            paGoalDay: data?.contractDetails?.paDays,
            paGoalPoints: data?.contractDetails?.paGoal,
            strategy1: data?.contractDetails?.strategy1,
            strategy2: data?.contractDetails?.strategy2,
            strategy3: data?.contractDetails?.strategy3,
            participantReward: data?.contractDetails?.participantReward,
            support1:
              data?.contractDetails?.support1 === null ||
              data?.contractDetails?.support1 === undefined
                ? true
                : data?.contractDetails?.support1,
            support2: data?.contractDetails?.support2,
            support3: data?.contractDetails?.support3,
            support4: data?.contractDetails?.support4,
            support5: data?.contractDetails?.support5,
            support6: data?.contractDetails?.support6,
            support7: data?.contractDetails?.support7,
            support8: data?.contractDetails?.support8,
            support9: data?.contractDetails?.support9,
            support10: data?.contractDetails?.support10,
            support11: data?.contractDetails?.support11,
            support12: data?.contractDetails?.support12,
            support13: data?.contractDetails?.support13,
            support14: data?.contractDetails?.support14,
            supportOtherText: data?.contractDetails?.supportOther,
            ...(data?.contractDetails?.supportOther
              ? { supportOther: true }
              : { supportOther: false }),
            adultReward: data?.contractDetails?.adultReward,
          },
          { emitEvent: false },
        );

        if (data.contractDetails.adultName) {
          this.form.patchValue(
            {
              adultName: data?.contractDetails?.adultName,
            },
            { emitEvent: false },
          );
        }

        if (data.contractDetails.adultSignature) {
          this.form.patchValue(
            {
              adultSignature: true,
              adultSignatureText: data?.contractDetails?.adultSignature,
            },
            { emitEvent: false },
          );
        }

        if (data.contractDetails.participantSignature) {
          this.form.patchValue(
            {
              childSignature: true,
              childSignatureText: data?.contractDetails?.participantSignature,
            },
            { emitEvent: false },
          );
        }

        if (data?.contractDetails?.stGoal !== null) {
          const hours = Math.floor(data?.contractDetails?.stGoal / 60);
          const minutes = data?.contractDetails?.stGoal % 60;

          this.form.patchValue(
            {
              stGoalHr: hours,
              stGoalMin: minutes,
            },
            { emitEvent: false },
          );
        } else {
          this.form.patchValue(
            {
              stGoalHr: null,
              stGoalMin: null,
            },
            { emitEvent: false },
          );
        }

        if (data?.contractDetails?.supportOther) {
          this.form.controls['supportOtherText'].enable();
        } else {
          this.form.controls['supportOtherText'].disable();
        }

        if (data?.contractDetails?.participantSignature) {
          this.form.controls['childSignatureText'].enable();
        } else {
          this.form.controls['childSignatureText'].disable();
        }

        if (data?.contractDetails?.adultSignature) {
          this.form.controls['adultSignatureText'].enable();
        } else {
          this.form.controls['adultSignatureText'].disable();
        }

        this.isContractSaved = data?.contractDetails?.createdAt ? true : false;
      });

    this.form.controls['supportOther'].valueChanges
      .pipe(takeUntil(this.onDestroy))
      .subscribe((data) => {
        if (data) {
          this.form.controls['supportOtherText'].enable();
        } else {
          this.form.controls['supportOtherText'].disable();
        }
      });

    this.form.controls['childSignature'].valueChanges
      .pipe(takeUntil(this.onDestroy))
      .subscribe((data) => {
        if (data) {
          this.form.controls['childSignatureText'].enable();
          this.form.controls['childSignatureText'].addValidators([
            Validators.required,
          ]);
          this.form.patchValue(
            {
              childSignatureText: `${this.participantName}`,
            },
            { emitEvent: false },
          );
        } else {
          this.form.controls['childSignatureText'].disable();
          this.form.patchValue(
            {
              childSignatureText: ``,
            },
            { emitEvent: false },
          );
          this.form.controls['childSignatureText'].removeValidators([
            Validators.required,
          ]);
        }
      });

    this.form.controls['adultSignatureText'].valueChanges
      .pipe(takeUntil(this.onDestroy))
      .subscribe((value) => {
        if (value) {
          this.form.controls['adultName'].setValue(value);
        } else {
          if (this.larAdult) {
            this.form.patchValue({
              adultName: `${this.larAdult.firstName} ${this.larAdult.lastName}`,
            });
          }
        }
      });

    this.form.controls['adultSignature'].valueChanges
      .pipe(takeUntil(this.onDestroy))
      .subscribe((data) => {
        if (data) {
          this.form.controls['adultSignatureText'].enable();
          this.form.controls['adultSignatureText'].addValidators([
            Validators.required,
          ]);
          this.form.patchValue(
            {
              adultSignatureText: `${this.larAdult.firstName} ${this.larAdult.lastName}`,
            },
            { emitEvent: false },
          );
        } else {
          this.form.controls['adultSignatureText'].disable();
          this.form.patchValue(
            {
              adultSignatureText: ``,
            },
            { emitEvent: false },
          );
          this.form.controls['adultSignatureText'].removeValidators([
            Validators.required,
          ]);
          // this.form.controls['adultName'].setValue(null);
        }
      });

      this.form.valueChanges
      .pipe(takeUntil(this.onDestroy))
      .subscribe(() => {
        if (this.contractDetails) {
          this.isContractDirty = this.form.dirty;
        }
      });
  }

  checkForErrors(currentField?: string) {
    this.errorMessages = {
      ...this.errorMessages,
      ...this.commonService.checkFormValidation(
        this.form,
        this.errorMessageMap,
        currentField,
      ),
    };
  }


  continue() {
    // ERROR CHECK IN THE FORM//
    if (this.form.invalid) {
      this.checkForErrors();

      // Check for stGoalMin validation error
      const stGoalMinControl = this.form.get('stGoalMin');
      if (
        stGoalMinControl &&
        (stGoalMinControl.hasError('min') || stGoalMinControl.hasError('max'))
      ) {
        this.commonService.notification(
          'Minutes must be between 0 and 59.',
          'danger',
          '',
          1000,
        );
        return;
      }

      return;
    }
    // EXTRACT VALUE FROM FORM//
    const {
      rlGoal,
      paGoalDay,
      paGoalPoints,
      stGoalHr,
      stGoalMin,
      strategy1,
      strategy2,
      strategy3,
      participantReward,
      adultReward,
      adultName,
      supportOther,
      supportOtherText,
      childSignature,
      childSignatureText,
      adultSignature,
      adultSignatureText,
      support1,
      support2,
      support3,
      support4,
      support5,
      support6,
      support7,
      support8,
      support9,
      support10,
      support11,
      support12,
      support13,
      support14,
    } = this.form.getRawValue();

    // check paGoalDay or paGoalPoints entered then make sure both values entered otherwise show error toast
    if (paGoalDay || paGoalPoints) {
      if (!paGoalDay) {
        this.commonService.notification(
          'Please enter physical activity goal days.',
          'danger',
        );
        return;
      }
      if (!paGoalPoints) {
        this.commonService.notification(
          'Please enter physical activity goal points.',
          'danger',
        );
        return;
      }
    }

    if (stGoalHr || stGoalMin) {
      this.stGoal = this.convertHoursToMins(stGoalHr, stGoalMin);
    }

    // PAYLOAD CREATION//
    const payload = {
      ...(rlGoal ? { rlGoal: Number(rlGoal) } : { rlGoal: null }),
      ...(this.sessionNumber >= 17
        ? { stGoal: stGoalHr === '' && stGoalHr === '' ? null : this.stGoal }
        : { stGoal: null }),
      ...(this.sessionNumber >= 9
        ? { paDays: paGoalDay === '' ? null : paGoalDay }
        : { paDays: null }),
      ...(this.sessionNumber >= 9
        ? { paGoal: paGoalPoints === '' ? null : paGoalPoints }
        : { paGoal: null }),
      strategy1,
      strategy2,
      strategy3,
      participantReward,
      adultReward,
      adultName,
      ...(supportOther ? { supportOther: supportOtherText } : {}),
      ...(childSignature ? { participantSignature: childSignatureText } : { participantSignature: null }),
      ...(adultSignature ? { adultSignature: adultSignatureText } : { adultSignature: null }),
      weekNumber: this.weekNumber,
      ...(this.route.snapshot.params['memberId']
        ? { forUserId: this.selectedUserId }
        : {}),
      ...(support1 ? { support1 } : { support1: false }),
      ...(support2 ? { support2 } : { support2: false }),
      ...(support3 ? { support3 } : { support3: false }),
      ...(support4 ? { support4 } : { support4: false }),
      ...(support5 ? { support5 } : { support5: false }),
      ...(support6 ? { support6 } : { support6: false }),
      ...(support7 ? { support7 } : { support7: false }),
      ...(support8 ? { support8 } : { support8: false }),
      ...(support9 ? { support9 } : { support9: false }),
      ...(support10 ? { support10 } : { support10: false }),
      ...(support11 ? { support11 } : { support11: false }),
      ...(support12 ? { support12 } : { support12: false }),
      ...(support13 ? { support13 } : { support13: false }),
      ...(support14 ? { support14 } : { support14: false }),
      participantName: this.contractDetails?.participantName,
      ...(this.route.snapshot.params['memberId']
        ? { groupId: this.selectedUserGroupId }
        : { groupId: this.groupId }),
    };
    // ACTION DISPATCH
    this.store.dispatch(
      UpdateContractDetailsRequest({ payload, previousUrl: this.previousUrl }),
    );
  }

  convertHoursToMins(hr: number, min: number) {
    let time = +hr * 60 + +min;
    return Number(time);
  }

  cancel() {
    if (!this.form.dirty) {
      this.location.back();
      return;
    }

    this.commonService.openConfirmationDialog({
      data: {
        message: 'All the Changes will be lost. Do you want to continue?',
        submitText: 'Yes',
        classList: 'w-[40vw]',
        handleSubmit: () => {
          this.commonService.closeDialog();
          this.location.back();
        },
        cancelText: 'No',
        handleCancel: () => {
          this.commonService.closeDialog();
        },
      },
    });
  }

  ngOnDestroy() {
    this.onDestroy.next(null);
    this.onDestroy.complete();
    this.store.dispatch(ClearWeekInfoForContract());
  }
}
